
import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Paper,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  RoomPreferences as RoomPreferencesIcon
} from '@mui/icons-material';

import { useUser, axiosService } from 'shared/helpers/axios';


const InfrastructureEditor = ({
  infrastructureItems,
  setInfrastructureItems,
  canEdit,
  addUrl,
  removeUrl,
  mutate,
  setSnackbar,
}) => {
  const { user: currentUser } = useUser();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newInfraName, setNewInfraName] = useState('');
  const [newInfraDesc, setNewInfraDesc] = useState('');
  const [newInfraCategory, setNewInfraCategory] = useState('');
  const [updating, setUpdating] = useState(false);

  const notify = (message, severity = 'success') => {
    if (setSnackbar) {
      setSnackbar({ open: true, message, severity });
    }
  };

  const handleAddInfrastructure = async () => {
    if (!newInfraName.trim()) return;

    try {
      setUpdating(true);
      const newItem = {
        name: newInfraName.trim(),
        description: newInfraDesc.trim(),
        category: newInfraCategory.trim(),
        added_by: currentUser?.username || currentUser?.email || '',
      };

      const { data: savedItem } = await axiosService.post(addUrl, newItem);
      setInfrastructureItems([...infrastructureItems, savedItem || newItem]);
      setNewInfraName('');
      setNewInfraDesc('');
      setNewInfraCategory('');
      setShowAddForm(false);
      notify('Infrastructure added');
      mutate();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to add infrastructure', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveInfrastructure = async (index) => {
    const snapshot = [...infrastructureItems];
    setInfrastructureItems(infrastructureItems.filter((_, i) => i !== index));
    try {
      setUpdating(true);
      await axiosService.delete(`${removeUrl}${index}/`);
      notify('Infrastructure removed');
      mutate();
    } catch (e) {
      setInfrastructureItems(snapshot); // rollback on failure
      notify(e.response?.data?.detail || 'Failed to remove infrastructure', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewInfraName('');
    setNewInfraDesc('');
    setNewInfraCategory('');
  };

  return (
    <Accordion
      defaultExpanded
      sx={{
        mb: 3,
        '&:before': { display: 'none' },
        boxShadow: 1,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="infrastructure-content"
        id="infrastructure-header"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 1 }}>
           
          <Typography variant="h6">
          <RoomPreferencesIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Infrastructure{infrastructureItems.length > 0 && ` (${infrastructureItems.length})`}
          </Typography>
          {canEdit && !showAddForm && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => { e.stopPropagation(); setShowAddForm(true); }}
            >
              Add
            </Button>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2, pb: 3 }}>
        {infrastructureItems.length > 0 ? (
          <Stack spacing={2} sx={{ mb: showAddForm ? 2 : 0 }}>
            {infrastructureItems.map((item, index) => (
              <Paper key={item.id ?? index} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600}>{item.name}</Typography>
                    {item.category && (
                      <Chip label={item.category} size="small" sx={{ mt: 0.5 }} />
                    )}
                    {item.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {item.description}
                      </Typography>
                    )}
                    {item.added_by && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                        Added by {item.added_by}
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveInfrastructure(index)}
                      disabled={updating}
                      title="Remove"
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>
        ) : !showAddForm ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No infrastructure added yet.
          </Typography>
        ) : null}

        {showAddForm && (
          <>
            {infrastructureItems.length > 0 && <Divider sx={{ my: 2 }} />}
            <Stack spacing={2}>
              <TextField
                label="Infrastructure Name *"
                value={newInfraName}
                onChange={(e) => setNewInfraName(e.target.value)}
                size="small"
                fullWidth
                autoFocus
              />
              <TextField
                label="Category"
                value={newInfraCategory}
                onChange={(e) => setNewInfraCategory(e.target.value)}
                size="small"
                fullWidth
                placeholder="e.g. Computing, Lab Equipment, Software"
              />
              <TextField
                label="Description"
                value={newInfraDesc}
                onChange={(e) => setNewInfraDesc(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleAddInfrastructure}
                  disabled={!newInfraName.trim() || updating}
                >
                  Save
                </Button>
                <Button variant="outlined" onClick={handleCancelAdd} disabled={updating}>
                  Cancel
                </Button>
              </Box>
            </Stack>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default InfrastructureEditor;
